/**
 * Portfolio Usage Terms component
 */
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ImageIcon } from "lucide-react";
import { PORTFOLIO_USAGE_OPTIONS, PORTFOLIO_RESTRICTIONS } from "@/lib/profileOptions";
import CheckBox from "./CheckBox";

export function PortfolioUsageTerms({
  portfolioUsage,
  setPortfolioUsage
}) {
  const updateUsage = (field, value) => {
    setPortfolioUsage({
      ...portfolioUsage,
      [field]: value
    });
  };

  return (
    <Card className="bg-white/[0.03] border-white/5 p-8 mb-6">
      <h2 className="text-xl font-semibold mb-2">
        <ImageIcon className="w-5 h-5 inline mr-2" />
        Portfolio Usage Policy
      </h2>
      <p className="text-white/50 mb-6">Can your team members use images from your shoots in their portfolios?</p>

      {/* Main selection */}
      <div className="space-y-3 mb-6">
        {PORTFOLIO_USAGE_OPTIONS.map((option) => (
          <div
            key={option.value}
            className={`flex items-center gap-3 p-4 bg-white/[0.05] border rounded-sm cursor-pointer transition-all ${portfolioUsage.allowed === option.value ? "border-white/40 bg-white/5" : "border-white/10"}`}
            onClick={() => updateUsage("allowed", option.value)}
            data-testid={`portfolio-usage-${option.value}`}
          >
            <CheckBox checked={portfolioUsage.allowed === option.value} />
            <span>{option.label}</span>
          </div>
        ))}
      </div>

      {/* Restrictions (only show if "with_restrictions" is selected) */}
      {portfolioUsage.allowed === "with_restrictions" && (
        <div className="mt-6 pt-6 border-t border-white/10">
          <h3 className="font-medium mb-4">Restrictions</h3>
          
          <div className="space-y-4">
            {/* After waiting period */}
            <div className="flex items-center gap-4">
              <div
                className={`flex items-center gap-3 p-3 bg-white/[0.05] border rounded-sm cursor-pointer transition-all flex-1 ${portfolioUsage.after_weeks ? "border-white/40 bg-white/5" : "border-white/10"}`}
                onClick={() => updateUsage("after_weeks", portfolioUsage.after_weeks ? null : 4)}
              >
                <CheckBox checked={!!portfolioUsage.after_weeks} />
                <span className="text-sm">After waiting period</span>
              </div>
              {portfolioUsage.after_weeks !== null && (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    value={portfolioUsage.after_weeks || ""}
                    onChange={(e) => updateUsage("after_weeks", parseInt(e.target.value) || null)}
                    className="w-20 bg-white/[0.05] border-white/10 text-white h-10 rounded-sm"
                  />
                  <span className="text-white/50 text-sm">weeks</span>
                </div>
              )}
            </div>

            {/* Other restrictions */}
            {PORTFOLIO_RESTRICTIONS.filter(r => r.value !== "after_weeks").map((restriction) => (
              <div
                key={restriction.value}
                className={`flex items-center gap-3 p-3 bg-white/[0.05] border rounded-sm cursor-pointer transition-all ${portfolioUsage[restriction.value] ? "border-white/40 bg-white/5" : "border-white/10"}`}
                onClick={() => updateUsage(restriction.value, !portfolioUsage[restriction.value])}
                data-testid={`restriction-${restriction.value}`}
              >
                <CheckBox checked={portfolioUsage[restriction.value]} />
                <span className="text-sm">{restriction.label}</span>
              </div>
            ))}

            {/* Additional terms (free text) */}
            <div className="mt-4">
              <Label className="text-sm text-white/60 uppercase tracking-wider">Additional Terms</Label>
              <Textarea
                value={portfolioUsage.additional_terms || ""}
                onChange={(e) => updateUsage("additional_terms", e.target.value)}
                placeholder="Any other restrictions or guidelines for your team..."
                className="mt-2 bg-white/[0.05] border-white/10 focus:border-white/40 text-white min-h-[100px] rounded-sm"
                data-testid="additional-terms-input"
              />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export default PortfolioUsageTerms;
